/**
 * ReferrerLoginPage
 * Login and registration for physiotherapists and other referrers
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Phone, Building, ArrowRight, UserPlus, LogIn, CheckCircle } from 'lucide-react';
import API_BASE_URL from '../config/api';
import { useReferrerPortalStatus } from '../utils/referrerPortalStatus';
import ReferralsDisabledNotice from '../components/ReferralsDisabledNotice';

function ReferrerLoginPage() {
  const navigate = useNavigate();
  const { submissionsEnabled, notice } = useReferrerPortalStatus();
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regPracticeName, setRegPracticeName] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regLetterEmail, setRegLetterEmail] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE_URL}/referrer-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.status === 'pending') {
          setError('Your account is pending approval. You will receive an email once approved.');
        } else {
          setError(data.error || 'Login failed');
        }
        return;
      }

      // Store token and referrer info
      sessionStorage.setItem('referrerToken', data.token);
      sessionStorage.setItem('referrer', JSON.stringify(data.referrer));
      
      navigate('/referrer/dashboard');
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (regPassword !== regConfirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (regPassword.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/referrer-auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: regName,
          email: regEmail,
          password: regPassword,
          practiceName: regPracticeName,
          phone: regPhone,
          letterEmail: regLetterEmail || regEmail
        })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Registration failed');
        return;
      }

      setSuccess('Registration submitted! Your account is pending approval. You will receive an email once approved.');
      // Clear form
      setRegName(''); setRegEmail(''); setRegPassword(''); setRegConfirmPassword('');
      setRegPracticeName(''); setRegPhone(''); setRegLetterEmail('');
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px 10px 40px',
    border: '1px solid #ddd',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
  };

  const iconStyle = {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999'
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f0f4f8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        maxWidth: 480,
        width: '100%',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #6b9bd1 0%, #4a7fb5 100%)',
          padding: '30px 30px 25px',
          textAlign: 'center',
        }}>
          <h1 style={{ color: 'white', margin: 0, fontSize: 24, fontWeight: 700 }}>
            ClinHx Referrer Portal
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', margin: '8px 0 0', fontSize: 14 }}>
            Anglesea Sports Medicine
          </p>
        </div>

        {/* Mode Toggle */}
        <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
          <button
            onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              borderBottom: mode === 'login' ? '3px solid #6b9bd1' : '3px solid transparent',
              backgroundColor: mode === 'login' ? '#f0f7ff' : 'white',
              cursor: 'pointer',
              fontWeight: mode === 'login' ? 600 : 400,
              color: mode === 'login' ? '#6b9bd1' : '#666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <LogIn size={16} /> Sign In
          </button>
          <button
            onClick={() => { setMode('register'); setError(''); setSuccess(''); }}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              borderBottom: mode === 'register' ? '3px solid #6b9bd1' : '3px solid transparent',
              backgroundColor: mode === 'register' ? '#f0f7ff' : 'white',
              cursor: 'pointer',
              fontWeight: mode === 'register' ? 600 : 400,
              color: mode === 'register' ? '#6b9bd1' : '#666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <UserPlus size={16} /> Register
          </button>
        </div>

        <div style={{ padding: 30 }}>
          {/* Shown before sign-in so referrers see it without having to log in. */}
          {!submissionsEnabled && (
            <ReferralsDisabledNotice notice={notice} style={{ marginBottom: 20 }} />
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              color: '#c33',
              padding: '10px 15px',
              borderRadius: 6,
              marginBottom: 20,
              fontSize: 14,
            }}>
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div style={{
              backgroundColor: '#e8f5e9',
              border: '1px solid #a5d6a7',
              color: '#2e7d32',
              padding: '15px',
              borderRadius: 6,
              marginBottom: 20,
              fontSize: 14,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
            }}>
              <CheckCircle size={20} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{success}</span>
            </div>
          )}

          {/* LOGIN FORM */}
          {mode === 'login' && (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 15, position: 'relative' }}>
                <Mail size={16} style={iconStyle} />
                <input
                  type="email"
                  placeholder="Email address"
                  value={loginEmail}
                  onChange={e => setLoginEmail(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 20, position: 'relative' }}>
                <Lock size={16} style={iconStyle} />
                <input
                  type="password"
                  placeholder="Password"
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#6b9bd1',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: loading ? 'wait' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'} <ArrowRight size={18} />
              </button>
            </form>
          )}

          {/* REGISTER FORM */}
          {mode === 'register' && !success && (
            <form onSubmit={handleRegister}>
              <div style={{ marginBottom: 15, position: 'relative' }}>
                <User size={16} style={iconStyle} />
                <input
                  type="text"
                  placeholder="Your full name"
                  value={regName}
                  onChange={e => setRegName(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 15, position: 'relative' }}>
                <Building size={16} style={iconStyle} />
                <input
                  type="text"
                  placeholder="Practice / clinic name"
                  value={regPracticeName}
                  onChange={e => setRegPracticeName(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 15, position: 'relative' }}>
                <Mail size={16} style={iconStyle} />
                <input
                  type="email"
                  placeholder="Email address (for login)"
                  value={regEmail}
                  onChange={e => setRegEmail(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 15, position: 'relative' }}>
                <Phone size={16} style={iconStyle} />
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={regPhone}
                  onChange={e => setRegPhone(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 15, position: 'relative' }}>
                <Mail size={16} style={iconStyle} />
                <input
                  type="email"
                  placeholder="Email for return letters (optional, defaults to login email)"
                  value={regLetterEmail}
                  onChange={e => setRegLetterEmail(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 15, position: 'relative' }}>
                <Lock size={16} style={iconStyle} />
                <input
                  type="password"
                  placeholder="Password (min 8 characters)"
                  value={regPassword}
                  onChange={e => setRegPassword(e.target.value)}
                  required
                  minLength={8}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 20, position: 'relative' }}>
                <Lock size={16} style={iconStyle} />
                <input
                  type="password"
                  placeholder="Confirm password"
                  value={regConfirmPassword}
                  onChange={e => setRegConfirmPassword(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#6b9bd1',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: loading ? 'wait' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                {loading ? 'Submitting...' : 'Register'} <ArrowRight size={18} />
              </button>
            </form>
          )}

          {/* Footer links */}
          <div style={{ marginTop: 20, textAlign: 'center', fontSize: 13, color: '#999' }}>
            <a href="/" style={{ color: '#6b9bd1', textDecoration: 'none', marginRight: 16 }}>
              ← Back to Home
            </a>
            <a href="/login" style={{ color: '#6b9bd1', textDecoration: 'none' }}>
              Staff login →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReferrerLoginPage;
